import React, { useEffect, useState } from 'react';
import { AlertCircle, ShieldCheck } from 'lucide-react';
import { Alert, Button, Checkbox, TaskOverlay } from '@parsina/framework';
import { C } from '@parsina/framework/tokens';
import { useMyPageStrings } from '../myPageStrings';
import { useErrorText } from './common';

// ─────────────────────────────────────────────────────────────────────────────
// «انتشار» — the IRIMC advertising-rules acknowledgement (design §D5) and the
// fact that the page is public on the internet, before the one call that makes
// it so. The checkbox is sent and recorded with the publish; the service
// refuses a publish without it.
//
// A refusal keeps the dialog open with the reasons inline (a form open in
// front of the doctor reports inline) — the same reasons also land on the
// offending section cards behind it.
// ─────────────────────────────────────────────────────────────────────────────

export default function PublishDialog({ open, onClose, onPublish, busy, error, isMobile, testId = 'my-page-publish' }) {
  const s = useMyPageStrings();
  const errorText = useErrorText();
  const [accepted, setAccepted] = useState(false);
  const [refusal, setRefusal] = useState([]);

  useEffect(() => { if (open) { setAccepted(false); setRefusal([]); } }, [open]);

  const submit = async () => {
    const res = await onPublish(accepted);
    if (res?.ok && res.published) onClose();
    else if (res?.ok) setRefusal(res.errors || []);
  };

  return (
    <TaskOverlay
      open={open}
      onClose={onClose}
      busy={busy}
      isMobile={isMobile}
      size="md"
      title={s('myPage.publish.title')}
      data-testid={testId}
      footer={(
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', width: '100%' }}>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid={`${testId}-cancel`}>
            {s('myPage.publish.cancel')}
          </Button>
          <Button onClick={submit} disabled={!accepted || busy} data-testid={`${testId}-confirm`}>
            {s('myPage.publish.confirm')}
          </Button>
        </div>
      )}
    >
      <div style={{ display: 'grid', gap: '1rem', fontSize: '0.8125rem', color: C.textPrimary, lineHeight: 1.9 }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
          <ShieldCheck size={18} aria-hidden style={{ color: C.navy, flexShrink: 0, marginTop: '0.25rem' }} />
          <p style={{ margin: 0 }}>{s('myPage.publish.intro')}</p>
        </div>
        <ul style={{ margin: 0, paddingInlineStart: '1.25rem', display: 'grid', gap: '0.375rem', color: C.textSecondary }}>
          <li>{s('myPage.publish.rule1')}</li>
          <li>{s('myPage.publish.rule2')}</li>
          <li>{s('myPage.publish.rule3')}</li>
          <li>{s('myPage.publish.rule4')}</li>
        </ul>
        <Alert variant="info">{s('myPage.publish.public')}</Alert>
        <Checkbox
          checked={accepted}
          onChange={(v) => setAccepted(v === true)}
          label={s('myPage.publish.accept')}
          disabled={busy}
          data-testid={`${testId}-accept`}
        />
        {refusal.length ? (
          <Alert variant="error" icon={<AlertCircle size={16} aria-hidden />} title={s('myPage.publish.failedTitle')} data-testid={`${testId}-errors`}>
            <ul style={{ margin: 0, paddingInlineStart: '1.1rem', display: 'grid', gap: '0.25rem' }}>
              {refusal.map((e, i) => <li key={`${e.code}-${i}`} dir="auto">{e.message}</li>)}
            </ul>
          </Alert>
        ) : null}
        {error ? <Alert variant="error">{errorText(error)}</Alert> : null}
      </div>
    </TaskOverlay>
  );
}
